import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ArrowRight,
  Sparkles,
  Radar,
  Brain,
  Workflow,
  Activity,
  TrendingUp,
  Quote,
  Target,
  Zap,
  Eye,
  CheckCircle2,
} from "lucide-react";
import { products, type Product } from "@/lib/products-data";
import { comparisons } from "@/lib/comparisons-data";

import { productMetrics } from "@/lib/page-metrics";
import {
  getCaseStudiesForProduct,
  getBlogCardsBySlugs,
  type BlogCard,
} from "@/lib/case-studies.functions";
import { getLatestGeoResources } from "@/lib/geo-resources.functions";
import { CaseStudiesSection } from "@/components/case-studies-section";
import { ComparisonsSection } from "@/components/comparisons-section";
import { GeoResourcesGallery } from "@/components/geo-resources-gallery";
import type { CaseStudy, GeoResource } from "@/lib/sanity";
import { GlassOrb } from "@/components/glass-orb";
import { SignalChip } from "@/components/signal-chip";
import { AuroraBackground } from "@/components/aurora-background";
import { SectionEyebrow } from "@/components/section-eyebrow";
import { GradientButton } from "@/components/gradient-button";
import { GlassIcon } from "@/components/glass-icon";
import { FinalCta } from "@/components/final-cta";
import { FaqSection } from "@/components/faq-section";
import { signUpUrl } from "@/lib/cta";
import { productModules } from "@/components/product-modules";
import { MetricCounters } from "@/components/metric-counters";
import { VsTable } from "@/components/vs-table";
import { OrbitLoop } from "@/components/orbit-loop";
import { seo } from "@/lib/seo";
import { softwareApplication, breadcrumb } from "@/lib/structured-data";
import { SITE_URL } from "@/lib/seo-constants";
import { InlineText } from "@/components/inline-text";

type LoaderData = {
  product: Product;
  caseStudies: CaseStudy[];
  geoResources: GeoResource[];
  blogCovers: BlogCard[];
};

// Curated blog posts surfaced on the GEO product page. Cover images are pulled
// from Sanity by slug (see loader) so the cards show real pictures.
const GEO_BLOG_CARDS = [
  {
    _id: "blog-geo-blueprint",
    slug: "a-practical-guide-to-aeo-geo-2026",
    title: "A practical guide to AEO / GEO in 2026",
    excerpt:
      "Build a GEO blueprint for Brand Citability that lifts your AI Visibility Score without constant manual input or oversight.",
    category: "Blog",
    externalUrl: "https://www.axy.digital/blog/a-practical-guide-to-aeo-geo-2026",
  },
  {
    _id: "blog-kpis-generative-search",
    slug: "shifting-kpis-for-the-generative-search-era-measuring-authority-over-traffic",
    title: "Shifting KPIs for the Generative Search Era: Measuring Authority Over Traffic",
    excerpt:
      "Why authority and citation metrics are replacing traffic as the core measure of search success in the age of AI discovery.",
    category: "Blog",
    externalUrl:
      "https://www.axy.digital/blog/shifting-kpis-for-the-generative-search-era-measuring-authority-over-traffic",
  },
] as const;

export const Route = createFileRoute("/products/$slug")({
  loader: async ({ params }): Promise<LoaderData> => {
    const product = products[params.slug];
    if (!product) throw notFound();
    const isGeo = params.slug === "geo-aeo-seo-autopilot";
    const [caseStudies, geoResources, blogCovers] = await Promise.all([
      getCaseStudiesForProduct({ data: { productSlug: params.slug } }),
      params.slug === "ai-visibility-analytics"
        ? getLatestGeoResources()
        : Promise.resolve([] as GeoResource[]),
      isGeo
        ? getBlogCardsBySlugs({ data: { slugs: GEO_BLOG_CARDS.map((c) => c.slug) } })
        : Promise.resolve([] as BlogCard[]),
    ]);
    return { product, caseStudies, geoResources, blogCovers };
  },
  head: ({ loaderData, params }) => {
    const p = loaderData?.product;
    if (!p) {
      return seo({
        title: "Product not found",
        description: "The product you're looking for doesn't exist or has been moved.",
        path: `/products/${params.slug}`,
        noindex: true,
      });
    }
    return seo({
      title: p.seoTitle,
      description: p.metaDescription,
      path: `/products/${params.slug}`,
      jsonLd: [
        softwareApplication({
          id: `${SITE_URL}/products/${params.slug}#product`,
          name: p.label,
          description: p.metaDescription,
          offers: [
            {
              name: "1 week free trial",
              price: 0,
              priceCurrency: "USD",
              url: `${SITE_URL}/pricing`,
            },
            {
              name: "Agentic Marketing (yearly)",
              url: `${SITE_URL}/pricing`,
            },
          ],
        }),
        breadcrumb([
          { name: "Home", url: "/" },
          { name: "Products", url: "/products" },
          { name: p.label, url: `/products/${params.slug}` },
        ]),
      ],
    });
  },
  component: ProductPage,
});

const featureIconCycle = [
  Radar,
  Eye,
  Sparkles,
  Workflow,
  Brain,
  Activity,
  Zap,
  Target,
  Quote,
  TrendingUp,
];
const flowIconCycle = [Radar, Brain, Sparkles, Workflow, Activity, Eye, Zap];

function ProductPage() {
  const loaded = Route.useLoaderData() as LoaderData;
  const data: Product = loaded.product;
  const caseStudies = loaded.caseStudies;
  const params = Route.useParams();
  const Module = productModules[params.slug];
  const metrics = productMetrics[params.slug] ?? [];

  // Attach each curated post's real cover image (fetched from Sanity by slug).
  // Intentionally no `slug` on the card so it links out via `externalUrl`
  // instead of the case-study route the section uses for internal slugs.
  const blogCards: CaseStudy[] =
    params.slug === "geo-aeo-seo-autopilot"
      ? GEO_BLOG_CARDS.map((c) => ({
          _id: c._id,
          title: c.title,
          excerpt: c.excerpt,
          category: c.category,
          externalUrl: c.externalUrl,
          coverImage: loaded.blogCovers.find((b) => b.slug?.current === c.slug)?.coverImage,
        }))
      : [];

  const whatsIncludedItems: { title: string; outcome: string }[] =
    params.slug === "geo-aeo-seo-autopilot"
      ? [
          {
            title: "Search demand analysis",
            outcome: "Capture high-intent search demand as it happens",
          },
          {
            title: "AI visibility gap detection",
            outcome: "Know exactly where you're invisible in AI search",
          },
          {
            title: "Competitor content gap analysis",
            outcome: "Find and close the gaps competitors are missing",
          },
          {
            title: "Prompt cluster mapping",
            outcome: "Build content around the real questions buyers ask",
          },
          {
            title: "Content brief generation",
            outcome: "Turn every gap into a ready-to-write brief",
          },
          {
            title: "SEO article generation",
            outcome: "Rank on Google and win long-tail discovery",
          },
          {
            title: "GEO article generation",
            outcome: "Get cited and recommended inside AI answers",
          },
          {
            title: "AEO article generation",
            outcome: "Answer customer questions at scale, formatted for AI",
          },
          {
            title: "Internal linking recommendations",
            outcome: "Strengthen authority across your site",
          },
          {
            title: "FAQ and structured content recommendations",
            outcome: "Cover more ground without more writing",
          },
          { title: "Content refresh workflows", outcome: "Keep ranked pages from going stale" },
          {
            title: "Cross-channel campaign generation",
            outcome: "Turn one piece of content into a full distribution push",
          },
        ]
      : [];

  const faqItems =
    params.slug === "geo-aeo-seo-autopilot"
      ? [
          {
            question: "What is GEO, AEO & SEO Autopilot?",
            answer:
              "It's an AI system that turns real-time search demand, AI prompts, and competitor gaps into SEO articles, GEO-optimized content, and AEO-style answers, then publishes and refines them continuously, so your brand ranks on Google and gets cited by AI search engines without manual work.",
          },
          {
            question: "How is this different from an AI writing tool like ChatGPT or Jasper?",
            answer:
              "Writing tools still need you to research, prompt, plan, and publish manually. GEO, AEO & SEO Autopilot runs the full loop itself: it detects demand, builds the content strategy, writes and publishes the content, distributes it for backlinks, and learns from what performs, with no prompting required.",
          },
          {
            question: "How much content does it produce, and how often?",
            answer:
              "By default, Axy.digital publishes around 30+ GEO articles per month, fully autonomously, though volume adjusts to your market's real demand and citation gaps rather than a fixed quota.",
          },
          {
            question: "Does it help me rank on Google, or just get cited by AI?",
            answer:
              "Both. It runs SEO, GEO, and AEO in one workflow, so the same content is built to rank in traditional search and get cited or recommended inside AI answers like ChatGPT, Perplexity, and Google AI Overviews.",
          },
          {
            question: "Does it also build backlinks and domain authority?",
            answer:
              "Yes, through Intelligent PR, an add-on that distributes press releases to publications frequently cited by AI search engines, earning high-authority backlinks.",
          },
          {
            question: "How long until I see results?",
            answer:
              "Results vary by market, but our Wingbits case study saw an 11x increase in AI visibility and climbed to #3 in its category within 90 days.",
          },
          {
            question: "Can I review content before it goes live?",
            answer:
              "Yes, you can review and approve content before it publishes, or run it fully autonomously if you'd rather not touch it.",
          },
        ]
      : [];

  const comparisonItems =
    params.slug === "geo-aeo-seo-autopilot"
      ? comparisons.map((c) => ({
          title: `Axy.digital vs ${c.competitor}`,
          description: `See how Axy.digital compares as the top ${c.competitor} alternative.`,
          href: `/comparisons/${c.slug}`,
        }))
      : [];

  const beforeList: string[] = data.problem.bullets ?? data.problem.body;
  const afterList: string[] = data.what.bullets ?? data.what.body;

  const flowSteps = data.how.steps.map((s, i) => ({
    label: s.title,
    description: s.body,
    icon: flowIconCycle[i % flowIconCycle.length],
  }));

  return (
    <article className="relative">
      {/* HERO */}
      <section className="relative px-6 pt-20 pb-20 overflow-hidden">
        <AuroraBackground />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <div>
            <SectionEyebrow>{data.label}</SectionEyebrow>
            <h1 className="text-5xl md:text-6xl font-semibold tracking-tight text-balance text-white mb-6 leading-[1.05]">
              {data.hero.title.split("\n").map((line, li, lineArr) => (
                <div key={li} className="contents">
                  {line.split(" ").map((w, i, arr) =>
                    i === arr.length - 1 && li === lineArr.length - 1 ? (
                      <span key={i} className="text-gradient">
                        {w}
                      </span>
                    ) : (
                      <span key={i}>{w} </span>
                    ),
                  )}
                  {li < lineArr.length - 1 && <br />}
                </div>
              ))}
            </h1>
            <p className="text-lg text-white/80 mb-8 text-pretty">{data.hero.tagline}</p>
            <div className="flex flex-wrap items-center gap-3 mb-10">
              <GradientButton asChild>
                <a href={signUpUrl("product-hero")}>
                  {data.hero.primaryCta} <ArrowRight className="size-4" />
                </a>
              </GradientButton>
            </div>
          </div>

          <div className="relative h-[28rem] hidden lg:block">
            <div className="absolute inset-0 grid place-items-center">
              <GlassOrb size="lg" />
            </div>
            <SignalChip
              label="Search demand rising"
              icon={TrendingUp}
              className="absolute top-4 left-0"
              delay={0}
            />
            <SignalChip
              label="Citation gap found"
              icon={Quote}
              className="absolute top-24 right-2"
              delay={1.2}
            />
            <SignalChip
              label="Narrative detected"
              icon={Target}
              className="absolute bottom-24 left-2"
              delay={2}
            />
            <SignalChip
              label="Opportunity found"
              icon={Zap}
              className="absolute bottom-6 right-6"
              delay={0.6}
            />
          </div>
          <div className="lg:hidden grid place-items-center pt-4">
            <GlassOrb size="md" />
          </div>
        </div>
      </section>

      {/* OUTCOME BAND */}
      {metrics.length > 0 && (
        <section className="px-6 -mt-2 mb-16">
          <div className="max-w-7xl mx-auto">
            <MetricCounters items={metrics} />
          </div>
        </section>
      )}

      {/* PRODUCT SUMMARY CARD */}
      {params.slug === "geo-aeo-seo-autopilot" && (
        <section className="px-6 -mt-4 mb-16">
          <div className="max-w-7xl mx-auto">
            <div className="glass-card p-6 md:p-8">
              <p className="text-white/85 text-lg md:text-xl leading-relaxed text-pretty">
                <strong className="text-white">GEO, AEO &amp; SEO Autopilot</strong> is an AI system
                that turns real-time search demand, AI prompts, and competitor gaps into SEO
                articles, GEO-optimized content, and AEO-style answers, then publishes and refines
                them continuously, so your brand ranks on Google and gets cited by AI search engines
                without manual work.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* PROBLEM — Vs Table */}
      <section className="px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-10">
            <SectionEyebrow>The Problem</SectionEyebrow>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance">
              {data.problem.heading}
            </h2>
            <div className="mt-5 space-y-3 text-white/70 text-pretty">
              {data.problem.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
          <VsTable before={beforeList.slice(0, 6)} after={afterList.slice(0, 6)} />
          {data.problem.closing && (
            <p className="mt-8 text-white/65 max-w-3xl text-pretty">{data.problem.closing}</p>
          )}
        </div>
      </section>

      {/* WHAT IT DOES + product module */}
      <section className="relative px-6 py-24 overflow-hidden">
        <AuroraBackground className="opacity-40" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <SectionEyebrow>{data.what.subheading}</SectionEyebrow>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance mb-6">
              {data.what.heading}
            </h2>
            <div className="space-y-3 text-white/75 text-pretty mb-6">
              {data.what.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            {data.what.bullets && (
              <ul className="grid sm:grid-cols-2 gap-2.5">
                {data.what.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-white/85">
                    <CheckCircle2 className="size-4 mt-0.5 text-pink-soft shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            )}
            {data.what.closing && (
              <p className="mt-6 text-white/70 text-pretty">{data.what.closing}</p>
            )}
          </div>
          <div>{Module ? <Module /> : null}</div>
        </div>
      </section>

      {/* HOW IT WORKS — orbit */}
      <section className="relative px-6 py-28 overflow-hidden">
        <AuroraBackground className="opacity-50" />
        <div className="relative max-w-7xl mx-auto">
          <div className="max-w-3xl mb-12">
            <SectionEyebrow>How it works</SectionEyebrow>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance">
              {data.how.heading}
            </h2>
          </div>
          <OrbitLoop steps={flowSteps} />
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="relative px-6 py-24 overflow-hidden">
        <AuroraBackground className="opacity-30" />
        <div className="relative max-w-7xl mx-auto">
          <div className="max-w-3xl mb-10">
            <SectionEyebrow>What's included</SectionEyebrow>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white">
              Every capability, and the outcome it drives
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {params.slug === "geo-aeo-seo-autopilot" && whatsIncludedItems.length > 0
              ? whatsIncludedItems.map((item, i) => {
                  const Icon = featureIconCycle[i % featureIconCycle.length];
                  return (
                    <div
                      key={item.title}
                      className="group glass-card p-5 flex items-start gap-3 hover-lift hover:-translate-y-1 hover:border-white/25 transition-all duration-300"
                      style={{ animationDelay: `${i * 60}ms` }}
                    >
                      <GlassIcon icon={Icon} size="sm" />
                      <div className="pt-1.5">
                        <p className="text-sm font-medium text-white">{item.title}</p>
                        <p className="text-sm text-white/60 mt-0.5">{item.outcome}</p>
                      </div>
                    </div>
                  );
                })
              : data.features.map((f, i) => {
                  const Icon = featureIconCycle[i % featureIconCycle.length];
                  return (
                    <div
                      key={f}
                      className="group glass-card p-5 flex items-start gap-3 hover-lift hover:-translate-y-1 hover:border-white/25 transition-all duration-300"
                      style={{ animationDelay: `${i * 60}ms` }}
                    >
                      <GlassIcon icon={Icon} size="sm" />
                      <p className="text-sm font-medium text-white pt-1.5">
                        <InlineText text={f} />
                      </p>
                    </div>
                  );
                })}
          </div>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="relative px-6 py-24 overflow-hidden">
        <AuroraBackground className="opacity-40" />
        <div className="relative max-w-3xl mx-auto text-center">
          <SectionEyebrow className="mx-auto inline-block">Why it matters</SectionEyebrow>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance mb-6">
            {data.why.heading}
          </h2>
          <div className="space-y-4 text-lg text-white/75 text-pretty">
            {data.why.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      <GeoResourcesGallery items={loaded.geoResources} />

      <CaseStudiesSection
        items={[...caseStudies, ...blogCards]}
        eyebrow="CASE STUDIES & RESOURCES"
        title="Practical tips, real outcomes"
      />

      <ComparisonsSection items={comparisonItems} />

      <FaqSection items={faqItems} />

      <FinalCta
        heading={data.closing.heading}
        body={data.closing.body}
        primaryCta={data.closing.primaryCta}
        primaryHref={signUpUrl("product-final")}
      />

      <div className="px-6 pb-24 -mt-10">
        <div className="max-w-7xl mx-auto text-center text-sm">
          <Link to="/products" className="text-pink-soft hover:text-white transition-colors">
            ← Back to all products
          </Link>
        </div>
      </div>
    </article>
  );
}
